<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  subscription: {
    type: Object,
    default: null,
  },
  trial: {
    type: Object,
    default: null,
  },
  access: {
    type: Object,
    default: null,
  },
});
const { t } = useI18n();

const statusLabels = {
  active: t('BILLING_SETTINGS.STATUS.ACTIVE'),
  trialing: t('BILLING_SETTINGS.STATUS.TRIAL'),
  active_trial: t('BILLING_SETTINGS.STATUS.TRIAL'),
  past_due: t('BILLING_SETTINGS.STATUS.PAST_DUE'),
  canceled: t('BILLING_SETTINGS.STATUS.CANCELED'),
  expired: t('BILLING_SETTINGS.STATUS.EXPIRED'),
  suspended: t('BILLING_SETTINGS.STATUS.SUSPENDED'),
  missing: t('BILLING_SETTINGS.STATUS.NO_PLAN'),
};
const accessLabels = {
  trial_expired: t('BILLING_SETTINGS.ACCESS.REASON.TRIAL_EXPIRED'),
  payment_grace_expired: t(
    'BILLING_SETTINGS.ACCESS.REASON.PAYMENT_GRACE_EXPIRED'
  ),
  subscription_ended: t('BILLING_SETTINGS.ACCESS.REASON.SUBSCRIPTION_ENDED'),
  subscription_missing: t(
    'BILLING_SETTINGS.ACCESS.REASON.SUBSCRIPTION_MISSING'
  ),
};

const status = computed(
  () => props.subscription?.status || props.trial?.status || 'missing'
);

const statusLabel = computed(() => statusLabels[status.value]);
const accessLabel = computed(() => accessLabels[props.access?.reason]);
const isTrialSubscription = computed(() =>
  ['trialing', 'active_trial'].includes(status.value)
);

const statusClasses = computed(() => {
  if (['past_due'].includes(status.value)) {
    return 'bg-n-amber-3 text-n-amber-11';
  }
  if (['canceled', 'expired', 'suspended'].includes(status.value)) {
    return 'bg-n-ruby-3 text-n-ruby-11';
  }
  if (['trialing', 'active_trial'].includes(status.value)) {
    return 'bg-n-blue-3 text-n-blue-11';
  }
  return 'bg-n-teal-3 text-n-teal-11';
});

const formatDate = value => {
  if (!value) return '';
  return new Intl.DateTimeFormat(undefined, { timeZone: 'UTC' }).format(
    new Date(value)
  );
};
</script>

<template>
  <div class="grid gap-4">
    <div class="flex flex-wrap items-center gap-3">
      <span
        data-test="subscription-status"
        class="rounded-md px-2 py-1 text-xs font-medium"
        :class="statusClasses"
      >
        {{ statusLabel }}
      </span>
      <span v-if="subscription?.plan_key" class="text-sm text-n-slate-11">
        {{ subscription.plan_key }}
      </span>
    </div>

    <p
      v-if="isTrialSubscription && trial?.ends_at"
      class="text-sm text-n-slate-11"
      data-test="trial-deadline"
    >
      {{
        $t('BILLING_SETTINGS.STATUS.TRIAL_ENDS_ON', {
          date: formatDate(trial.ends_at),
        })
      }}
    </p>

    <p
      v-if="status === 'active' && subscription?.current_period?.ends_at"
      class="text-sm text-n-slate-11"
      data-test="renewal-deadline"
    >
      {{
        $t('BILLING_SETTINGS.STATUS.RENEWS_ON', {
          date: formatDate(subscription.current_period.ends_at),
        })
      }}
    </p>

    <p
      v-if="isTrialSubscription && Number.isInteger(trial?.days_remaining)"
      class="text-sm text-n-slate-11"
      data-test="trial-countdown"
    >
      <template v-if="trial.days_remaining === 1">
        {{ $t('BILLING_SETTINGS.TRIAL_BANNER.REMAINING_ONE') }}
      </template>
      <template v-else>
        {{
          $t('BILLING_SETTINGS.TRIAL_BANNER.REMAINING', {
            days: trial.days_remaining,
          })
        }}
      </template>
    </p>

    <div
      v-if="subscription?.grace?.ends_at"
      class="rounded-lg bg-n-amber-2 p-3 text-sm text-n-amber-11"
      data-test="grace-deadline"
    >
      {{
        $t('BILLING_SETTINGS.STATUS.GRACE_ENDS_ON', {
          date: formatDate(subscription.grace.ends_at),
        })
      }}
    </div>

    <p v-if="status === 'past_due'" class="text-sm text-n-amber-11">
      {{ $t('BILLING_SETTINGS.STATUS.PAST_DUE_NOTE') }}
    </p>

    <p
      v-if="subscription?.cancellation?.access_ends_at"
      class="text-sm text-n-slate-11"
      data-test="cancellation-deadline"
    >
      {{
        $t('BILLING_SETTINGS.STATUS.CANCELED_ENDS_ON', {
          date: formatDate(subscription.cancellation.access_ends_at),
        })
      }}
    </p>

    <div
      v-if="subscription?.pending_change"
      class="rounded-lg bg-n-blue-2 p-3 text-sm text-n-blue-11"
      data-test="pending-plan-change"
    >
      <span>{{ $t('BILLING_SETTINGS.PLAN_CHANGE.PENDING') }}</span>
      <strong class="mx-1 font-medium">
        {{ subscription.pending_change.to_plan_key }}
      </strong>
      <span>
        {{
          $t('BILLING_SETTINGS.PLAN_CHANGE.EFFECTIVE_ON', {
            date: formatDate(subscription.pending_change.effective_at),
          })
        }}
      </span>
    </div>

    <div
      v-if="access?.mode === 'read_only'"
      class="rounded-lg bg-n-ruby-2 p-3 text-sm text-n-ruby-11"
      data-test="read-only-access"
    >
      <p class="font-medium">
        {{ $t('BILLING_SETTINGS.ACCESS.READ_ONLY.TITLE') }}
      </p>
      <p class="mt-1">{{ accessLabel }}</p>
    </div>
  </div>
</template>
